import { FC, useEffect, useState, useCallback } from "react"
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core"
import { ButtonEcomm, Paragraph } from "@ncs/web-legos/components"
import { TrackingEvent } from "@ncs/web-legos/util"
import { useOrderTotals } from "~/contexts"
import { useHistory, useLocation } from "react-router-dom"
import { DialogContentStyle, DialogTitleStyle, DialogButtonStyle, DialogBodyStyle } from "./styles"
import { useAnalytics } from "~/hooks/useAnalytics"

const LOCAL_STORAGE_KEY = "abandonCartModalShown"
const EXCLUDED_ROUTES = ["/shop/checkout", "/shop/cart-summary"]

const isBrowser = typeof window !== "undefined"

const ShopBarAbandonCartModal: FC = () => {
	const { totalItemCount } = useOrderTotals(false)
	const { pathname } = useLocation()
	const history = useHistory()
	const [showModal, setShowModal] = useState(false)
	const { trackEvent } = useAnalytics()

	const hasModalBeenShown = useCallback((): boolean => {
		if (!isBrowser) return false
		return localStorage.getItem(LOCAL_STORAGE_KEY) === "true"
	}, [])

	const markModalAsShown = useCallback((): void => {
		if (!isBrowser) return
		localStorage.setItem(LOCAL_STORAGE_KEY, "true")
	}, [])

	const isRouteExcluded = useCallback((path: string): boolean => {
		return EXCLUDED_ROUTES.some((route) => path.startsWith(route))
	}, [])

	const canShowModal = useCallback((): boolean => {
		return totalItemCount > 0 && !hasModalBeenShown()
	}, [totalItemCount, hasModalBeenShown])

	// Effect 1: Detect Back Navigation
	useEffect(() => {
		if (!isBrowser) return

		const unblock = history.block((location, action) => {
			if (action === "POP" && canShowModal()) {
				const isSourceExcluded = isRouteExcluded(pathname)
				const isTargetExcluded = isRouteExcluded(location.pathname)

				// Only show if we are not navigating TO an excluded route
				if (!isTargetExcluded) {
					setShowModal(true)
					markModalAsShown()

					// If we are on a normal page, block navigation to show modal here.
					if (!isSourceExcluded) {
						return false
					}
				}
			}
			return undefined
		})

		return () => unblock()
	}, [history, pathname, canShowModal, isRouteExcluded, markModalAsShown])

	// Effect 2: Detect Tab Change
	useEffect(() => {
		if (!isBrowser) return

		const handleVisibilityChange = () => {
			if (
				document.visibilityState === "visible" &&
				canShowModal() &&
				!isRouteExcluded(pathname)
			) {
				setShowModal(true)
				markModalAsShown()
			}
		}

		document.addEventListener("visibilitychange", handleVisibilityChange)

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange)
		}
	}, [canShowModal, isRouteExcluded, pathname, markModalAsShown])

	// Effect 3: Detect Exit Intent (Mouse leaving viewport)
	useEffect(() => {
		if (!isBrowser) return

		const handleMouseLeave = (event: MouseEvent) => {
			// Only trigger if mouse leaves from the top (intent to close tab/address bar)
			if (event.clientY <= 0 && canShowModal() && !isRouteExcluded(pathname)) {
				setShowModal(true)
				markModalAsShown()
			}
		}

		document.addEventListener("mouseleave", handleMouseLeave)

		return () => {
			document.removeEventListener("mouseleave", handleMouseLeave)
		}
	}, [canShowModal, isRouteExcluded, pathname, markModalAsShown])

	// Effect 4: Track modal view
	useEffect(() => {
		if (showModal) {
			trackEvent(TrackingEvent.EXIT_MODAL_VIEWED)
		}
	}, [showModal, trackEvent])

	const handleClose = () => {
		trackEvent(TrackingEvent.EXIT_MODAL_DISMISSED)
		setShowModal(false)
		markModalAsShown()
	}

	const handleCheckout = () => {
		trackEvent(TrackingEvent.EXIT_MODAL_CHECKOUT_CLICKED)
		markModalAsShown()
		setShowModal(false)
		history.push("/shop/checkout")
	}

	return (
		<Dialog open={showModal} onClose={handleClose} fullWidth>
			<div css={DialogBodyStyle}>
				<DialogTitle css={DialogTitleStyle}>🚗 🧽 Leaving already?</DialogTitle>
				<DialogContent css={DialogContentStyle}>
					<Paragraph>
						Your cart is waiting for you. <br /> Checkout now and keep your carwash
						running.
					</Paragraph>
				</DialogContent>
				<DialogActions>
					<ButtonEcomm
						variant="outline-cta"
						css={DialogButtonStyle}
						onClick={handleClose}
					>
						I'LL BE BACK
					</ButtonEcomm>
					<ButtonEcomm
						variant="primary-cta"
						css={DialogButtonStyle}
						onClick={handleCheckout}
					>
						CHECKOUT
					</ButtonEcomm>
				</DialogActions>
			</div>
		</Dialog>
	)
}

export default ShopBarAbandonCartModal
